<template>
  <v-dialog v-model="dialog" persistent max-width="1200px">
    <template v-slot:activator="{ on }">
      <v-btn v-if="isEditButton" icon v-on="on">
        <v-icon>fa-edit</v-icon>
      </v-btn>
      <v-btn v-else color="info" dark v-on="on">
        <v-icon class="pr-1">fa-plus-circle</v-icon>
          Add New
      </v-btn>
    </template>

    <v-card class="ma-0 pa-0">
      <v-card-title class="">
        <span class="text-h5">Cycle</span>
      </v-card-title>

      <v-card-text class="ma-0 pa-0">
          <v-data-table
            :headers="cycleHeader"
            :footer-props="footerProps"
            :hide-default-footer="true"
            :items="workingCycle"
            fixed-header
            dense
            id="editCycleTable">
            <template v-slot:header="" >
              <thead>
                <tr>
                  <th colspan="2"></th>
                  <th colspan="2">Statement</th>
                  <th colspan="2">Qualifications</th>
                  <th colspan="2">Earnings</th>
                </tr>
              </thead>
            </template>

            <template v-slot:item="{ item }">
              <tr>
                <td> <text-box-date-picker v-model="item.dropDate"
                        ref="dropDate"/></td>
                <td> <text-box-date-picker v-model="item.secondDropDate"
                        ref="secondDropDate"/></td>
                <td> <text-box-date-picker v-model="item.statementStartDate"
                        ref="statementStartDate" @input="onChangeStatementStartDate()"/></td>
                <td> <text-box-date-picker v-model="item.statementEndDate"
                        ref="statementEndDate" @input="onChangeStatementEndDate()"/></td>
                <td> <text-box-date-picker v-model="item.qualificationStartDate"
                        ref="qualificationStartDate"/></td>
                <td> <text-box-date-picker v-model="item.qualificationEndDate"
                        ref="qualificationEndDate"/></td>
                <td> <text-box-date-picker v-model="item.earningsStartDate"
                        ref="earningsStartDate" :disabled="earningsSameAsStatement"/></td>
                <td> <text-box-date-picker v-model="item.earningsEndDate"
                        ref="earningsEndDate" :disabled="earningsSameAsStatement"/></td>
              </tr>
            </template>

          </v-data-table>
      </v-card-text>

      <v-card-text>
        <v-col>
          <v-checkbox hide-details v-model="earningsSameAsStatement"
            @change="onChangeEarningsSameAsStatement()"
            ref="earningsSameAsStatementCheckbox" label="Earnings Cycle is Statement Cycle">
          </v-checkbox>
        </v-col>

        <!-- Display validation Errors -->
        <v-col v-if="validationErrors.length>0" style="background-color: rgb(243, 245, 195)">
          <span v-for="item in validationErrors" :key="item"> {{item}} <br> </span>
        </v-col>
      </v-card-text>

      <v-card-actions >
        <v-spacer></v-spacer>
        <v-btn ref="cancelButton" @click="cancel" :disabled="saving">Cancel</v-btn>
        <v-btn ref="saveButton" class="blue white--text" @click="save()"
          :disabled="validationErrors.length>0 || !isDataModified" :loading="saving" >Save</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import Vue from 'vue';
import { mapState } from 'vuex';
import { DataTableHeader } from 'vuetify';
import {
  Component, Prop, Watch, Inject,
} from 'vue-property-decorator';
import {
  Cycle,
  CycleResource,
} from '@/api/cycles';
import { TextBoxDatePicker } from '@kasasa/portal-components';
import { Alert, NoticeClass } from '@kasasa/fbase-components/lib';

@Component({
  computed: mapState(['fiId']),
  components: {
    'text-box-date-picker': TextBoxDatePicker,
  },
})
export default class CycleDialog extends Vue {
  @Inject('CycleResource')
  cycleResource!:CycleResource;

  @Prop()
  cycle!: Cycle;

  @Prop()
  isEditButton!:boolean;

  @Prop()
  cycleSetId!: number;

  @Prop()
  hasProcessingDays!: boolean;

  workingCycle:Cycle[] = new Array(CycleDialog.createEmptyCycle());

  fiId!:string;

  dialog:boolean = false;

  saving:boolean = false;

  loading = false;

  earningsSameAsStatement: boolean = true;

  validationErrors: string[] = [];

  isDataModified: boolean = false;

  cycleHeader:DataTableHeader[] = [
    { text: 'Drop Date', value: 'dropDate', sortable: false },
    { text: '2nd Drop Date', value: 'secondDropDate', sortable: false },
    { text: 'Start', value: 'statementStartDate', sortable: false },
    { text: 'End', value: 'statementEndDate', sortable: false },
    { text: 'Start', value: 'qualificationStartDate', sortable: false },
    { text: 'End', value: 'qualificationEndDate', sortable: false },
    { text: 'Start', value: 'earningsStartDate', sortable: false },
    { text: 'End', value: 'earningsEndDate', sortable: false },
  ];

  footerProps = {
    'items-per-page-options': [{ value: -1, text: 'All' }, 25, 50, 100],
  }

  static createEmptyCycle() : Cycle {
    return {
      id: undefined!,
      cycleSetId: undefined!,
      dropDate: '',
      secondDropDate: '',
      statementStartDate: '',
      statementEndDate: '',
      qualificationStartDate: '',
      qualificationEndDate: '',
      rewardsSweepDate: '',
      earningsStartDate: '',
      earningsEndDate: '',
      qualificationRollForwardDate: '',
      earningsRollForwardDate: '',
      hasAssociatedResults: false,
      endOfMonthDateInCycle: '',
    };
  }

  @Watch('dialog')
  onDialogToggle() {
    if (this.dialog) {
      this.saving = false;
      this.loading = true;
      this.initializeWorkingCycle();
      this.loading = false;
    }
  }

  initializeWorkingCycle() {
    this.validationErrors = [];
    this.isDataModified = false;
    this.workingCycle = new Array(CycleDialog.createEmptyCycle());

    this.earningsSameAsStatement = (this.workingCycle[0].statementStartDate === this.workingCycle[0].earningsStartDate)
      && (this.workingCycle[0].statementEndDate === this.workingCycle[0].earningsEndDate);

    if (this.isEditButton) {
      const copyCycle: Cycle = CycleDialog.createEmptyCycle();
      Object.assign(copyCycle, this.cycle);
      this.workingCycle = new Array(copyCycle);
    }

    if (!this.isEditButton && this.cycle) { // Initialize based on end dates of prev cycle
      this.workingCycle[0].cycleSetId = this.cycle.cycleSetId;
      this.workingCycle[0].statementStartDate = this.addOneDay(this.cycle.statementEndDate);
      this.workingCycle[0].qualificationStartDate = this.addOneDay(this.cycle.qualificationEndDate);
      this.workingCycle[0].earningsStartDate = this.addOneDay(this.cycle.earningsEndDate);
    }
    if (this.cycleSetId) { // Get the cycleSetId for add new Cycle when there there is no prev cycle
      this.workingCycle[0].cycleSetId = this.cycleSetId;
    }
  }

  parseDate = (date: string) => {
    const parts = date.split('-');
    return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  }

  addOneDay(dateString: string) {
    const date = this.parseDate(dateString);
    date.setDate(date.getDate() + 1);
    return date.toISOString().substring(0, 10);
  }

  cancel() {
    this.initializeWorkingCycle();
    this.dialog = false;
  }

  save() {
    this.performValidation();
    if (this.validationErrors.length > 0) {
      return;
    }
    this.saving = true;
    this.cycleResource.saveCycle(this.fiId, this.workingCycle[0])
      .then((savedCycle: Cycle) => {
        this.$emit('success', savedCycle);
        this.dialog = false;
        this.initializeWorkingCycle();
      })
      .catch((error:any) => {
        this.saving = false;
        if (error && error.response && error.response.status === 409) {
          this.setError('Cycle cannot be saved because it conflicts with existing cycles');
        } else if (error.response && error.response.data && error.response.data.errorMessage) {
          this.setError(error.response.data.errorMessage);
        } else if (error.response && error.response.data && error.response.data.entity) {
          this.setError(error.response.data.entity);
        } else {
          this.setError('Unexpected error saving cycle');
        }
      });
  }

  noDataModified() {
    return this.workingCycle[0] && this.cycle
        && this.workingCycle[0].dropDate === this.cycle!.dropDate
        && this.workingCycle[0].secondDropDate === this.cycle!.secondDropDate
        && this.workingCycle[0].statementStartDate === this.cycle!.statementStartDate
        && this.workingCycle[0].statementEndDate === this.cycle!.statementEndDate
        && this.workingCycle[0].qualificationStartDate === this.cycle!.qualificationStartDate
        && this.workingCycle[0].qualificationEndDate === this.cycle!.qualificationEndDate
        && this.workingCycle[0].earningsStartDate === this.cycle!.earningsStartDate
        && this.workingCycle[0].earningsEndDate === this.cycle!.earningsEndDate;
  }

  @Watch('workingCycle', { deep: true })
  performValidation() {
    if (this.noDataModified()) {
      this.isDataModified = false;
      return;
    }

    this.isDataModified = true;
    this.validationErrors = [];

    if (!this.hasProcessingDays) {
      this.validationErrors.push('Processing Days Must be configured before creating cycles!');
    }
    if (!this.workingCycle[0].dropDate) {
      this.validationErrors.push('Drop Date is required!');
    }
    if (!this.workingCycle[0].secondDropDate) {
      this.validationErrors.push('2nd Drop Date is required!');
    }
    if (new Date(this.workingCycle[0].dropDate).getTime()
        >= new Date(this.workingCycle[0].secondDropDate).getTime()) {
      this.validationErrors.push('2nd Drop Date must be after Drop Date!');
    }
    if (!this.workingCycle[0].statementStartDate) {
      this.validationErrors.push('Statement Start Date is required!');
    }
    if (!this.workingCycle[0].statementEndDate) {
      this.validationErrors.push('Statement End Date is required!');
    }
    if (new Date(this.workingCycle[0].statementStartDate).getTime()
        >= new Date(this.workingCycle[0].statementEndDate).getTime()) {
      this.validationErrors.push('Statement End Date must be after Statement Start Date!');
    }
    if (!this.workingCycle[0].qualificationStartDate) {
      this.validationErrors.push('Qualification Start Date is required!');
    }
    if (!this.workingCycle[0].qualificationEndDate) {
      this.validationErrors.push('Qualification End Date is required!');
    }
    if (new Date(this.workingCycle[0].qualificationStartDate).getTime()
        >= new Date(this.workingCycle[0].qualificationEndDate).getTime()) {
      this.validationErrors.push('Qualification End Date must be after Qualification Start Date!');
    }
    if (new Date(this.workingCycle[0].dropDate).getTime()
        < new Date(this.workingCycle[0].qualificationEndDate).getTime()) {
      this.validationErrors.push('Qualification End Date must be equal or prior to dropDate!');
    }
    if (!this.workingCycle[0].earningsStartDate) {
      this.validationErrors.push('Earnings Start Date is required!');
    }
    if (!this.workingCycle[0].earningsEndDate) {
      this.validationErrors.push('Earnings End Date is required!');
    }
    if (new Date(this.workingCycle[0].earningsStartDate).getTime()
        >= new Date(this.workingCycle[0].earningsEndDate).getTime()) {
      this.validationErrors.push('Earnings End Date must be after Earnings Start Date!');
    }
  }

  onChangeEarningsSameAsStatement() {
    if (this.earningsSameAsStatement) {
      this.workingCycle[0].earningsStartDate = this.workingCycle[0].statementStartDate;
      this.workingCycle[0].earningsEndDate = this.workingCycle[0].statementEndDate;
    }
  }

  onChangeStatementStartDate() {
    if (this.earningsSameAsStatement) {
      this.workingCycle[0].earningsStartDate = this.workingCycle[0].statementStartDate;
    }
  }

  onChangeStatementEndDate() {
    if (this.earningsSameAsStatement) {
      this.workingCycle[0].earningsEndDate = this.workingCycle[0].statementEndDate;
    }
  }

  setError(message:string) {
    const al = new Alert(message, NoticeClass.ERROR);
    al.setTimeout(5000);
    this.$store.dispatch('notices/add', al);
  }
}
</script>

<style lang="scss" scoped>
</style>
